var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, styled } from '@mui/material';
import { useCreatePath, useListContext, sanitizeListRestProps, useUpdate, useNotify, useRefresh, useSidebarState, } from 'react-admin';
import isEqual from 'lodash/isEqual';
import { getFilterValuesFromInterval as defaultGetFilterValuesFromInterval } from './getFilterValuesFromInterval';
import { convertToEvent as defaultConvertToEvent } from './convertToEvent';
import { useNavigate } from 'react-router-dom';
/**
 * Calendar view, based on full-calendar's <FullCalendar> component
 *
 * Uses the react-admin Redux store as content provider, and links to the edit
 * and create views of the current resource.
 *
 * Must be used inside a `<ListContext>`. Prefereably use this component as a
 * child of `<List>`.
 *
 * Additional props are passed to `<FullCalendar>`.
 *
 * @example
 * ```
 * import { Calendar, getFilterValuesFromInterval } from '@react-admin/ra-calendar';
 * import { Admin, Resource, List, Edit, SimpleForm, TextInput, DateTimeInput } from 'react-admin';
 *
 * const EventList: FC<ComponentProps<typeof List>> = () => (
 *     <List
 *         filterDefaultValues={getFilterValuesFromInterval()}
 *         perPage={1000}
 *         pagination={false}
 *     >
 *         <Calendar />
 *     </List>
 * );
 *
 * const EventEdit: FC<ComponentProps<typeof Edit>> = () => (
 *     <Edit>
 *         <SimpleForm>
 *             <TextInput source="title" />
 *             <DateTimeInput source="start" />
 *             <DateTimeInput source="end" />
 *         </SimpleForm>
 *     </Edit>
 * );
 *
 * const EventCreate: FC<ComponentProps<typeof Create>> = () => (
 *     <Create>
 *         <SimpleForm>
 *             <TextInput source="title" />
 *             <DateTimeInput source="start" />
 *             <DateTimeInput source="end" />
 *         </SimpleForm>
 *     </Create>
 * );
 *
 * export const App: FC = () => (
 *     <Admin dataProvider={dataProvider}>
 *         <Resource
 *             name="events"
 *             list={EventList}
 *             edit={EventEdit}
 *             create={EventCreate}
 *         />
 *     </Admin>
 * );
 * ```
 */
export var Calendar = function (props) {
    var notify = useNotify();
    var refresh = useRefresh();
    var navigate = useNavigate();
    var createPath = useCreatePath();
    var defaultOnSuccess = function () {
        notify('ra.notification.updated', {
            type: 'info',
            messageArgs: {
                smart_count: 1,
            },
            undoable: true,
        });
    };
    var defaultOnError = function (error) {
        notify(typeof error === 'string'
            ? error
            : error.message || 'ra.notification.http_error', { type: 'warning' });
        refresh();
    };
    var className = props.className, _a = props.getFilterValueFromInterval, getFilterValueFromInterval = _a === void 0 ? defaultGetFilterValuesFromInterval : _a, _b = props.convertToEvent, convertToEvent = _b === void 0 ? defaultConvertToEvent : _b, _c = props.mutationMode, mutationMode = _c === void 0 ? 'undoable' : _c, _d = props.mutationOptions, mutationOptions = _d === void 0 ? {
        onSuccess: defaultOnSuccess,
        onError: defaultOnError,
    } : _d, sx = props.sx, transform = props.transform, rest = __rest(props, ["className", "getFilterValueFromInterval", "convertToEvent", "mutationMode", "mutationOptions", "sx", "transform"]);
    var _e = useListContext(), data = _e.data, filterValues = _e.filterValues, setFilters = _e.setFilters, displayedFilters = _e.displayedFilters, resource = _e.resource;
    var updateEvent = useUpdate()[0];
    var calendar = useRef();
    // update size when sidebar changes
    var isFirstRender = useRef(true);
    useEffect(function () {
        isFirstRender.current = false;
    }, []);
    var sidebarIsOpen = useSidebarState()[0];
    useEffect(function () {
        if (!isFirstRender.current) {
            setTimeout(function () {
                if (calendar.current) {
                    calendar.current.getApi().updateSize();
                }
            }, 300); // the max duration of the sidebar transition
        }
    }, [sidebarIsOpen]);
    var handleDatesSet = function (dateInfo) {
        var newFilterValues = getFilterValueFromInterval(dateInfo, filterValues);
        if (!isEqual(filterValues, newFilterValues)) {
            setFilters(newFilterValues, displayedFilters);
        }
    };
    var handleDateSelect = function (selectInfo) {
        var calendarApi = selectInfo.view.calendar;
        calendarApi.unselect(); // clear date selection
        navigate("/".concat(resource, "/create"), {
            state: {
                record: {
                    start: selectInfo.startStr,
                    end: selectInfo.endStr,
                    allDay: selectInfo.allDay,
                },
            },
        });
    };
    var handleEventClick = function (clickInfo) {
        navigate(createPath({ resource: resource, id: clickInfo.event.id, type: 'edit' }));
    };
    var handleEventChange = function (changeInfo) { return __awaiter(void 0, void 0, void 0, function () {
        var payload, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _b = {
                        id: changeInfo.event.id
                    };
                    if (!transform) return [3 /*break*/, 2];
                    return [4 /*yield*/, transform(changeInfo.event.toPlainObject())];
                case 1:
                    _a = _c.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = changeInfo.event.toPlainObject();
                    _c.label = 3;
                case 3:
                    payload = (_b.data = _a,
                        _b);
                    updateEvent(resource, payload, __assign(__assign({}, mutationOptions), { mutationMode: mutationMode }));
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(Root, { className: className, sx: sx },
        React.createElement(FullCalendar, __assign({ ref: calendar, plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin], headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }, initialView: "dayGridMonth", editable: true, selectable: true, selectMirror: true, dayMaxEvents: true, navLinks: true, datesSet: handleDatesSet, select: handleDateSelect, events: data === null || data === void 0 ? void 0 : data.map(convertToEvent), eventClick: handleEventClick, eventChange: handleEventChange }, sanitizeListRestProps(rest)))));
};
var PREFIX = 'RaCalendar';
var Root = styled(Box, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var theme = _a.theme;
    return ({
        padding: '1em',
        // material-ui skin for full calendar
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        '& .fc-toolbar-title': {
            fontSize: theme.typography.h5.fontSize,
            fontWeight: theme.typography.h5.fontWeight,
        },
        '& .fc-button': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.action.active,
            borderColor: theme.palette.divider,
            fontSize: theme.typography.button.fontSize,
            fontWeight: theme.typography.button.fontWeight,
            textTransform: theme.typography.button.textTransform,
        },
        '& .fc-button-primary:disabled': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.action.selected,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
            color: theme.palette.action.active,
        },
        '& .fc-button-group .fc-button': {
            borderColor: theme.palette.divider,
            color: theme.palette.action.active,
        },
        '& .fc-button:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
        '& .fc-button-primary:not(:disabled):active': {
            backgroundColor: theme.palette.action.active,
            color: theme.palette.text.primary,
        },
        '& .fc-button-group .fc-button-primary:not(:disabled):active': {
            borderColor: theme.palette.divider,
        },
        '& .fc-button-primary:not(:disabled).fc-button-active': {
            backgroundColor: theme.palette.action.selected,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
            color: theme.palette.action.active,
        },
        '& .fc-button-group .fc-button-primary:not(:disabled).fc-button-active': {
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
        },
        '& .fc-button-primary:not(:disabled):focus': {
            boxShadow: 'none',
        },
        '& .fc-button-primary:not(:disabled):active:focus': {
            boxShadow: 'none',
        },
        '& .fc-button-primary:not(:disabled).fc-button-active:focus': {
            boxShadow: 'none',
        },
        '& .fc-popover': {
            backgroundColor: theme.palette.background.paper,
        },
    });
});
